import React, {useEffect, useState} from 'react';
import {
    useGetAllProductSegmentsQuery,
    useGetCategoryMappingsByContextQuery, 
    useSaveCategoryMappingMutation
} from "../../store/endpoints/ProductCategorizationEndpoints";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import {IProductCategoryMapping, IProductSegment} from "../../models/Storefront";
import DropdownField from "../Common/DropdownField";
import {IconButton, MessageBar, MessageBarType} from "@fluentui/react";

interface ICategoryMappingTableProps {
    context: string;
    externalSegments: {segmentName: string, id: string}[]
    externalTypes: {typeName: string, id: string}[]
    onSelectSegment: (segment: IProductSegment) => void;
}

const ExtensionSegmentMappingTable: React.FC<ICategoryMappingTableProps> = (props) => {
    
    const segments = useGetAllProductSegmentsQuery()
    const mappings = useGetCategoryMappingsByContextQuery({context: props.context}, {skip: !props.context})
    
    const [columns, setColumns] = React.useState<IColumnDetails[]>([]);
    const [saveCategoryMapping] = useSaveCategoryMappingMutation();
    const [saveStatus, setSaveStatus] = useState<string>("");
    const [saveStatusType, setSaveStatusType] = useState<MessageBarType>(MessageBarType.info);

    // Hide the save message after a few seconds if successful
    useEffect(() => {
        if (saveStatusType === MessageBarType.success && saveStatus) {
            const timeout = setTimeout(() => {
                setSaveStatus("");
            }, 2500);
            return () => clearTimeout(timeout);
        }
    }, [saveStatus, saveStatusType]);

    useEffect(() => {
        setColumns([
            {
                index: 0,
                identifier: 'editCategories',
                label: '',
                type: 'Text',
                required: true,
                component: (v: IProductSegment) => <IconButton iconProps={{iconName: 'edit'}} onClick={() => {props.onSelectSegment(v)}}/>
            },
            {
                index: 1,
                identifier: 'segment',
                label: 'Segment',
                type: 'Text',
                required: true,
                component: (v: IProductSegment) => <div>{v.identifier}</div>
            },
            {
                index: 2,
                identifier: 'externalProductType',
                label: 'External Product Type',
                type: 'Text',
                required: true,
                component: (cat: IProductSegment) => {
                    let mappingForCategory = mappings.data?.items.find((mapping) => mapping.id === `${props.context.normalizeIdentifier()}-${cat.id}`);
                    return (
                        <DropdownField
                            selectedKey={mappingForCategory?.externalProductTypeId}
                            placeholder={!props.externalTypes || props.externalTypes.length === 0 ? 'Loading Product Types' : 'Select Product Type'}
                            disabled={!props.externalTypes || props.externalTypes.length === 0}
                            options={props.externalTypes.map((cat) => ({key: cat.id, text: cat.typeName}))}
                            onChange={(e, v) => {
                                if(!mappingForCategory) {
                                    mappingForCategory = {
                                        id: '',
                                        segment: cat?.identifier ?? '',
                                        category: cat?.id,
                                        externalSegmentId: v?.key as string ?? '',
                                        externalCategoryId: undefined,
                                        context: props.context
                                    }
                                }
                                saveCategoryMapping({
                                    mapping: {...mappingForCategory, externalProductTypeId: v?.key as string ?? ''},
                                    context: props.context
                                }).then((result) => {
                                    if ('data' in result) {
                                        setSaveStatus("Mapping saved successfully");
                                        setSaveStatusType(MessageBarType.success);
                                    } else {
                                        setSaveStatus("Failed to save mapping");
                                        setSaveStatusType(MessageBarType.error);
                                    }
                                });
                            }}
                        />

                    );
                }
            },
            {
                index: 3,
                identifier: 'externalSegment',
                label: 'External Category',
                type: 'Text',
                required: true,
                component: (cat: IProductSegment) => {
                    const mappingForCategory = mappings.data?.items.find((mapping) => mapping.id === `${props.context.normalizeIdentifier()}-${cat.id}`);
                    return (
                        <div style={{paddingRight: '10px'}}>
                            <DropdownField
                                placeholder={!props.externalSegments || props.externalSegments.length === 0 ? 'Loading Product Categories' : 'Select Product Category'}
                                selectedKey={mappingForCategory?.externalSegmentId}
                                options={props.externalSegments.map((cat) => ({ key: cat.id, text: cat.segmentName }))}
                                onChange={(e, v) => {
    
                                    const mappingForCategory = { id: '', segment: cat?.identifier ?? '', category: cat?.id, externalSegmentId: v?.key as string ?? '', externalCategoryId: undefined , context: props.context }
                                    saveCategoryMapping({mapping: mappingForCategory, context: props.context }).then((result) => {
                                        if ('data' in result) {
                                            setSaveStatus("Mapping saved successfully");
                                            setSaveStatusType(MessageBarType.success);
                                        } else {
                                            setSaveStatus("Failed to save mapping");
                                            setSaveStatusType(MessageBarType.error);
                                        }
                                    });
                                }}
                            />
                        </div>
                    );
                }
            }
        ]);
    }, [mappings.data, props.externalSegments, props.externalTypes])

    return (
        <div>
            <DataGrid items={segments.data?.items ?? []} selectionMode={GridSelectionMode.none} loading={segments.isLoading || segments.isFetching} getItemId={(item: IProductSegment) => item.id ?? ''} overrideColumns={columns} />
            {saveStatus && (
                <MessageBar messageBarType={saveStatusType} isMultiline={false} styles={{root: {marginLeft: 8, minWidth: 200}}}>
                    {saveStatus}
                </MessageBar>
            )}
        </div>
    )
}

export default ExtensionSegmentMappingTable;